import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Brand palette sampled directly from the RITIS mark (public/avatars/RITIS.png):
// teal diamond ≈ #5DC3AE, charcoal diamond ≈ #565759. The splash uses a tight
// two-tone accent system built from those two colors (plus neutral whites/grays)
// instead of the previous unrelated blue/green/orange mix, so the loading
// animation actually reads as the same brand as the logo it's introducing.
const TEAL = "#5DC3AE";
const TEAL_SOFT = "rgba(93,195,174,.9)";
const SILVER = "#AEB6C2";

interface SplashScreenProps {
  onFinish: () => void;
}

export default function SplashScreen({ onFinish }: SplashScreenProps) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 100);
    const t2 = setTimeout(() => setStep(2), 1400);
    const t3 = setTimeout(() => setStep(3), 2400);
    const t4 = setTimeout(() => onFinish(), 3100);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [onFinish]);

  const particles = [
    { cx: "7%", cy: "14%", r: 1.2, fill: "rgba(93,195,174,.16)" },
    { cx: "87%", cy: "9%", r: 0.8, fill: "rgba(174,182,194,.14)" },
    { cx: "94%", cy: "54%", r: 1.5, fill: "rgba(93,195,174,.1)" },
    { cx: "3%", cy: "71%", r: 1.0, fill: "rgba(174,182,194,.1)" },
    { cx: "71%", cy: "89%", r: 0.6, fill: "rgba(93,195,174,.18)" },
    { cx: "24%", cy: "87%", r: 1.3, fill: "rgba(174,182,194,.08)" },
    { cx: "51%", cy: "3%", r: 0.9, fill: "rgba(93,195,174,.14)" },
    { cx: "17%", cy: "41%", r: 0.7, fill: "rgba(174,182,194,.09)" },
    { cx: "79%", cy: "27%", r: 1.1, fill: "rgba(93,195,174,.13)" },
    { cx: "41%", cy: "77%", r: 0.5, fill: "rgba(174,182,194,.1)" },
    { cx: "59%", cy: "17%", r: 0.8, fill: "rgba(93,195,174,.1)" },
    { cx: "34%", cy: "5%", r: 1.0, fill: "rgba(174,182,194,.08)" },
    { cx: "63%", cy: "96%", r: 0.7, fill: "rgba(93,195,174,.08)" },
    { cx: "11%", cy: "58%", r: 0.9, fill: "rgba(174,182,194,.07)" },
  ];

  const steps = [
    { label: "Auth", done: true, active: false },
    { label: "Projects", done: true, active: false },
    { label: "Data", done: false, active: true },
    { label: "Dashboard", done: false, active: false, pending: true },
  ];

  return (
    <AnimatePresence>
      {step < 3 && (
        <motion.div
          key="splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "radial-gradient(circle at 50% 40%, #0b141d 0%, #05070f 55%, #030408 100%)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            overflow: "hidden",
            fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
          }}
        >

          {/* ── Ambient glow behind the mark ── */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4 }}
            style={{
              position: "absolute", top: "38%", left: "50%", transform: "translate(-50%,-50%)",
              width: 520, height: 520, borderRadius: "50%", pointerEvents: "none",
              background: "radial-gradient(circle, rgba(93,195,174,.09) 0%, rgba(93,195,174,0) 70%)",
            }}
          />

          {/* ── Particles ── */}
          <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
            {particles.map((p, i) => <circle key={i} cx={p.cx} cy={p.cy} r={p.r} fill={p.fill} />)}
          </svg>

          {/* ── Center content ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}
          >

            {/* Ring system */}
            <div style={{ position: "relative", width: 200, height: 200, marginBottom: 36 }}>

              {/* Slow spinning rings */}
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                style={{ position: "absolute", inset: -20, borderRadius: "50%", border: "1px solid rgba(93,195,174,0.14)" }} />
              <motion.div animate={{ rotate: -360 }} transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "1px solid rgba(174,182,194,0.1)" }} />

              {/* SVG draw rings */}
              <svg style={{ position: "absolute", inset: -12, width: "calc(100% + 24px)", height: "calc(100% + 24px)" }} viewBox="0 0 224 224">
                <circle cx="112" cy="112" r="106" fill="none" stroke="rgba(93,195,174,0.16)" strokeWidth="1" strokeDasharray="4 8" />
                <motion.circle cx="112" cy="112" r="96" fill="none" stroke="rgba(93,195,174,0.4)" strokeWidth="1.5"
                  strokeDasharray="603" strokeLinecap="round"
                  initial={{ strokeDashoffset: 603 }} animate={{ strokeDashoffset: 0 }}
                  transition={{ duration: 1.4, delay: 0.2, ease: [0.4, 0, 0.2, 1] }} />
                <motion.circle cx="112" cy="112" r="76" fill="none" stroke="rgba(174,182,194,0.22)" strokeWidth="1"
                  strokeDasharray="478"
                  initial={{ strokeDashoffset: 478 }} animate={{ strokeDashoffset: 0 }}
                  transition={{ duration: 1.2, delay: 0.5, ease: [0.4, 0, 0.2, 1] }} />
              </svg>

              {/* Orbiting dots — teal + silver, matching the two mark colors */}
              {[
                { dur: 5, dir: 1, size: 8, color: TEAL, glow: "rgba(93,195,174,.9)", pos: { top: -4, left: "50%", marginLeft: -4 } },
                { dur: 8, dir: -1, size: 6, color: SILVER, glow: "rgba(174,182,194,.85)", pos: { bottom: -3, left: "50%", marginLeft: -3 } },
              ].map((o, i) => (
                <motion.div key={i}
                  animate={{ rotate: 360 * o.dir }}
                  transition={{ duration: o.dur, repeat: Infinity, ease: "linear" }}
                  style={{ position: "absolute", inset: -16, borderRadius: "50%" }}>
                  <div style={{
                    position: "absolute", ...o.pos,
                    width: o.size, height: o.size, borderRadius: "50%",
                    background: o.color, boxShadow: `0 0 12px ${o.glow},0 0 24px ${o.glow.replace(".9", ".4").replace(".85", ".4")}`,
                  }} />
                </motion.div>
              ))}

              {/* Logo — circular, on a white badge so both mark colors stay legible on dark */}
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
                  transition={{
                    opacity: { duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] },
                    scale: { duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] },
                    y: { duration: 4, delay: 1.5, repeat: Infinity, ease: "easeInOut" },
                  }}
                  style={{
                    width: 130, height: 130, borderRadius: "50%",
                    background: "#fff", padding: 22,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 0 0 1px rgba(255,255,255,.15),0 24px 80px rgba(0,0,0,.6),0 0 60px rgba(93,195,174,.18)",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src="/avatars/RITIS.png"
                    alt="RITIS"
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    onError={e => {
                      const img = e.target as HTMLImageElement;
                      img.style.display = "none";
                      const parent = img.parentNode as HTMLElement | null;
                      if (parent) {
                        parent.style.background = TEAL;
                        parent.innerHTML = '<span style="color:#fff;font-weight:900;font-size:42px">R</span>';
                      }
                    }}
                  />
                </motion.div>
              </div>
            </div>

            {/* Brand text */}
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.32em", textTransform: "uppercase", color: "rgba(255,255,255,.88)", marginBottom: 12 }}>
                Al Rammah International
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 10 }}>
                <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 1.4, duration: 0.8 }}
                  style={{ height: 1, width: 72, background: TEAL, transformOrigin: "center" }} />
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
                  style={{ width: 5, height: 5, borderRadius: "50%", background: TEAL, boxShadow: "0 0 8px rgba(93,195,174,.7)" }} />
                <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 1.4, duration: 0.8 }}
                  style={{ height: 1, width: 72, background: TEAL, transformOrigin: "center" }} />
              </div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: TEAL_SOFT }}>
                IT & Telecom Services Co. — RITIS
              </div>
            </motion.div>

            {/* Stats strip — single accent color, reads as one system rather than a rainbow */}
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.6 }}
              style={{ display: "flex", gap: 20, marginBottom: 28, alignItems: "center" }}>
              {[
                { val: "200+", lbl: "Projects" },
                { val: "300+", lbl: "Clients" },
                { val: "100+", lbl: "Employees" },
                { val: "1999", lbl: "Est." },
              ].map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                    <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em", lineHeight: 1 }}>{s.val}</div>
                    <div style={{ fontSize: 8, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(148,163,184,.4)" }}>{s.lbl}</div>
                  </div>
                  {i < 3 && <div style={{ width: 1, height: 32, background: "rgba(255,255,255,.06)" }} />}
                </div>
              ))}
            </motion.div>

            {/* Progress bar */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
              style={{ width: 200, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
                <span style={{ fontSize: 9, fontWeight: 600, color: "rgba(148,163,184,.4)", letterSpacing: "0.06em" }}>Initializing...</span>
                <motion.span
                  animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }}
                  style={{ fontSize: 10, fontWeight: 800, color: TEAL }}>···</motion.span>
              </div>
              <div style={{ height: 2.5, background: "rgba(255,255,255,.06)", borderRadius: 99, overflow: "hidden" }}>
                <motion.div
                  initial={{ width: "5%" }} animate={{ width: "88%" }}
                  transition={{ duration: 2.5, delay: 0.5, ease: [0.4, 0, 0.2, 1] }}
                  style={{ height: "100%", borderRadius: 99, background: TEAL, position: "relative", overflow: "hidden" }}>
                  <motion.div
                    animate={{ x: ["-100%", "300%"] }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg,transparent,rgba(255,255,255,.4),transparent)", borderRadius: 99 }} />
                </motion.div>
              </div>
            </motion.div>

            {/* Step indicators */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
              style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 36 }}>
              {steps.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <motion.div
                    animate={s.active ? { scale: [1, 1.5, 1], opacity: [1, 0.5, 1] } : {}}
                    transition={{ duration: 1.2, repeat: Infinity }}
                    style={{
                      width: s.active ? 8 : 6, height: s.active ? 8 : 6, borderRadius: "50%",
                      background: s.done ? TEAL : s.active ? SILVER : "rgba(255,255,255,.12)",
                      boxShadow: s.done ? "0 0 6px rgba(93,195,174,.5)" : s.active ? "0 0 10px rgba(174,182,194,.6)" : "none",
                      flexShrink: 0,
                    }} />
                  <span style={{
                    fontSize: 9, fontWeight: 700,
                    color: s.done ? TEAL : s.active ? SILVER : "rgba(148,163,184,.35)",
                  }}>{s.label}</span>
                  {i < steps.length - 1 && <span style={{ color: "rgba(255,255,255,.08)", fontSize: 10, margin: "0 1px" }}>—</span>}
                </div>
              ))}
            </motion.div>

            {/* Footer */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}
              style={{ fontSize: 8, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(148,163,184,.18)" }}>
              Innovation <i style={{ fontStyle: "italic" }}>Through</i> Technology
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
